import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MaskLines } from "@/components/site/Motion";
import { PhotoPicker } from "@/components/apply/PhotoPicker";
import { PhotoExamples } from "@/components/apply/PhotoExamples";
import { VideoPicker, uploadVideo } from "@/components/apply/VideoPicker";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", gender: "", height: "", bust: "", waist: "", hips: "", shoes: "", hair: "", eyes: "", birth_date: "", city: "", nationality: "", instagram: "", message: "", parent_name: "", parent_email: "", parent_phone: "", mother_agency: "" };
const MEASURES = [["bust", "Bust (cm)", "number", "82"], ["waist", "Waist (cm)", "number", "60"], ["hips", "Hips (cm)", "number", "88"],
  ["shoes", "Shoes (EU)", "text", "39"], ["hair", "Hair colour", "text", "Brown"], ["eyes", "Eye colour", "text", "Green"]];

const ageOf = (d) => {
  if (!d) return null;
  const b = new Date(d), t = new Date();
  return t.getFullYear() - b.getFullYear() - ((t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) ? 1 : 0);
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70">{label}{required && <span className="text-[#1c1418]"> *</span>}</span>
    {children}
  </label>
);

const inputCls = "mt-2 w-full border-b border-[#1c1418]/30 bg-transparent py-3 text-lg text-[#1c1418] outline-none transition-colors placeholder:text-[#1c1418]/30 focus:border-[#1c1418]";

export default function Apply() {
  const [f, setF] = useState(EMPTY);
  const [ref] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("ref");
    if (q) sessionStorage.setItem("youth_ref", q.toLowerCase());
    return sessionStorage.getItem("youth_ref") || "";
  });
  const [photos, setPhotos] = useState([null, null, null, null]);
  const [video, setVideo] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const age = ageOf(f.birth_date);
  const minor = age !== null && age < 18;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.gender) return toast.error("Please select your gender.");
    if (!f.height) return toast.error("Please enter your height.");
    setSending(true);
    try {
      const body = { ...f, ref, height: Number(f.height), bust: Number(f.bust), waist: Number(f.waist), hips: Number(f.hips), photos: photos.filter(Boolean) };
      if (!minor) Object.assign(body, { parent_name: "", parent_email: "", parent_phone: "" });
      const { data } = await api.post("/applications", body);
      if (video) {
        try {
          await uploadVideo(data.id, data.upload_token, video, setProgress);
        } catch {
          toast.error("Your application was sent, but the video upload failed.");
        }
      }
      setDone(data);
      toast.success("Application sent to our scouts.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  if (done) {
    return (
      <main data-testid="apply-success" className="flex min-h-screen items-center bg-white px-5 pt-24 text-[#1c1418] md:px-10">
        <div className="mx-auto max-w-3xl">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-[#1c1418] text-white"><Check className="h-8 w-8 text-[#e9458f]" /></div>
          <MaskLines lines={["Thank you,", `${f.first_name}.`]} className="mt-8 font-display text-7xl uppercase leading-[0.88] md:text-9xl" />
          <p className="mt-8 max-w-xl font-serif text-2xl italic text-[#1c1418]/80">Your application has reached our scouting team. If your profile fits, we will contact you by email or phone.</p>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="apply-page" className="bg-white px-5 pb-28 pt-32 text-[#1c1418] md:px-10">
      <div className="mx-auto grid max-w-[1400px] gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div>
            <p className="w-fit border border-[#1c1418] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]">Scouting · Open call</p>
            <MaskLines lines={["Youth", "is yours."]} className="mt-8 font-display text-7xl uppercase leading-[0.86] md:text-8xl lg:text-[7.5rem]" />
            <p data-testid="apply-hashtag" className="mt-4 font-display text-4xl leading-none md:text-6xl">#youthisyours</p>
            <p className="mt-8 max-w-md font-serif text-xl italic text-[#1c1418]/80">Natural light, no make-up, no filters. Three simple photos are all we need.</p>
            <p data-testid="apply-requirement" className="mt-10 w-fit bg-[#1c1418] px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white">
              We are looking for female models · minimum height 173 cm
            </p>
            <PhotoExamples />
          </div>
        </div>
        <motion.form onSubmit={submit} data-testid="apply-form" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9 }}
          className="space-y-10 self-start border border-[#1c1418]/15 p-6 md:p-10 lg:col-span-7">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70">Gender <span className="text-[#1c1418]">*</span></span>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {[["F", "Woman"], ["M", "Man"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => setF((s) => ({ ...s, gender: v }))} data-testid={`apply-gender-${v}`}
                  className={`border py-4 font-display text-3xl uppercase transition-colors ${f.gender === v ? "border-[#1c1418] bg-[#1c1418] text-white" : "border-[#1c1418]/30 hover:border-[#1c1418]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-8 sm:grid-cols-2">
            <Field label="Height (cm)" required><input data-testid="apply-height-input" type="number" min="120" max="230" required value={f.height} onChange={set("height")} placeholder="175" className={inputCls} /></Field>
            <Field label="Date of birth" required><input data-testid="apply-birth-date-input" type="date" required value={f.birth_date} onChange={set("birth_date")} max={new Date().toISOString().slice(0, 10)} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3" data-testid="apply-measures">
            {MEASURES.map(([k, label, type, ph]) => (
              <Field key={k} label={label} required>
                <input data-testid={`apply-${k}-input`} type={type} required value={f[k]} onChange={set(k)} placeholder={ph}
                  {...(type === "number" ? { min: k === "waist" ? 40 : 50, max: k === "waist" ? 130 : 150 } : { maxLength: k === "shoes" ? 10 : 40, minLength: k === "shoes" ? 1 : 2 })} className={inputCls} />
              </Field>
            ))}
          </div>
          {minor && (
            <div data-testid="apply-parent-section" className="space-y-6 bg-[#f4f4f4] p-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em]">Under 18 · Parent or guardian contact *</p>
              <div className="grid gap-6 sm:grid-cols-3">
                <Field label="Full name" required><input data-testid="apply-parent-name-input" required value={f.parent_name} onChange={set("parent_name")} className={inputCls} /></Field>
                <Field label="Email" required><input data-testid="apply-parent-email-input" type="email" required value={f.parent_email} onChange={set("parent_email")} className={inputCls} /></Field>
                <Field label="Phone" required><input data-testid="apply-parent-phone-input" required value={f.parent_phone} onChange={set("parent_phone")} className={inputCls} /></Field>
              </div>
            </div>
          )}
          <div className="grid gap-8 sm:grid-cols-2">
            <Field label="First name" required><input data-testid="apply-first-name-input" required value={f.first_name} onChange={set("first_name")} className={inputCls} /></Field>
            <Field label="Last name" required><input data-testid="apply-last-name-input" required value={f.last_name} onChange={set("last_name")} className={inputCls} /></Field>
            <Field label="Email" required><input data-testid="apply-email-input" type="email" required value={f.email} onChange={set("email")} className={inputCls} /></Field>
            <Field label="Phone" required><input data-testid="apply-phone-input" required value={f.phone} onChange={set("phone")} className={inputCls} /></Field>
            <Field label="City"><input data-testid="apply-city-input" value={f.city} onChange={set("city")} className={inputCls} /></Field>
            <Field label="Nationality"><input data-testid="apply-nationality-input" value={f.nationality} onChange={set("nationality")} className={inputCls} /></Field>
            <Field label="Instagram"><input data-testid="apply-instagram-input" value={f.instagram} onChange={set("instagram")} placeholder="@" className={inputCls} /></Field>
            <Field label="Mother agency (if you have one)"><input data-testid="apply-mother-agency-input" value={f.mother_agency} onChange={set("mother_agency")} placeholder="Agency name" className={inputCls} /></Field>
          </div>
          <Field label="Anything else?"><textarea data-testid="apply-message-input" rows={3} value={f.message} onChange={set("message")} className={inputCls} /></Field>
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70">Polas · natural photos (max 4)</span>
            <div className="mt-3"><PhotoPicker photos={photos} setPhotos={setPhotos} /></div>
          </div>
          <VideoPicker video={video} setVideo={setVideo} progress={progress} />
          <button type="submit" disabled={sending} data-testid="apply-submit-button"
            className="flex w-full items-center justify-between bg-[#1c1418] px-6 py-5 font-mono text-sm uppercase tracking-[0.25em] text-white transition-transform hover:-translate-y-1 disabled:opacity-60">
            {sending ? (progress !== null ? `Uploading video ${progress}%` : "Sending…") : "Send application"} <ArrowUpRight className="h-5 w-5 text-[#e9458f]" />
          </button>
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#1c1418]/60">Applicants under 18 need parental consent. Your data is used only for scouting.</p>
        </motion.form>
      </div>
    </main>
  );
}
